import { useEffect, useRef } from 'react';
import { lock, unlock } from 'tua-body-scroll-lock';

/**
 * A hook that extends the "tua-body-scroll-lock" package.
 * @param shouldLockBody - if "true", it will lock the body and only the "targetElement" can be scrolled.
 * @param targetElement "targetElement" needs to be defined or it won't lock the body. The "targetElement" will be scrollable when the body is locked.
 * @returns "lock" and "unlock" functions for testing purposes, you shouldn't need to use these.
 */
export const useBodyScrollLock = (shouldLockBody: boolean, targetElement: HTMLElement | null) => {
  const isBodyAlreadyLocked = useRef<boolean>(false);

  useEffect(() => {
    if (!targetElement) {
      return;
    }

    if (shouldLockBody && !isBodyAlreadyLocked.current) {
      lock(targetElement);
      isBodyAlreadyLocked.current = true;
    } else if (!shouldLockBody && isBodyAlreadyLocked.current) {
      unlock(targetElement);
      isBodyAlreadyLocked.current = false;
    }

    return () => {
      if (targetElement && isBodyAlreadyLocked.current) {
        unlock(targetElement);
        isBodyAlreadyLocked.current = false;
      }
    };
  }, [shouldLockBody, targetElement, isBodyAlreadyLocked]);

  return { lock, unlock };
};
